import React from 'react';
import './ErrorBoundary.css';
import i18n from '../i18n';
import { formatErrorForDisplay } from '../utils/errorHandler';

const CHUNK_RELOAD_KEY = 'oikra:chunk-reload';

function isChunkLoadError(error) {
  if (!error) return false;
  const name = error.name || '';
  const message = error.message || String(error);
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\w.-]+ failed/i.test(message) ||
    /ChunkLoadError/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message)
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    if (isChunkLoadError(error) && typeof window !== 'undefined') {
      try {
        const alreadyReloaded = sessionStorage.getItem(CHUNK_RELOAD_KEY) === '1';
        if (!alreadyReloaded) {
          sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
          if ('serviceWorker' in navigator) {
            navigator.serviceWorker.getRegistrations().then((regs) => {
              regs.forEach((reg) => reg.unregister());
            });
          }
          if (window.caches && caches.keys) {
            caches.keys().then((keys) => Promise.all(keys.map((k) => caches.delete(k))));
          }
          window.location.reload();
          return;
        }
        sessionStorage.removeItem(CHUNK_RELOAD_KEY);
      } catch {
        /* ignore storage failures */
      }
    }

    try {
      if (typeof window !== 'undefined' && window.Sentry && typeof window.Sentry.captureException === 'function') {
        window.Sentry.captureException(error, { extra: errorInfo });
      }
    } catch (reportError) {
      console.warn('Error reporting to Sentry:', reportError);
    }
    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    if (this.props.resetOnError) {
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      const chunkFail = isChunkLoadError(this.state.error);
      const errorDisplay = chunkFail
        ? {
            title: i18n.t('errorBoundary.chunkTitle', {
              defaultValue: 'App update available',
            }),
            message: i18n.t('errorBoundary.chunkMessage', {
              defaultValue:
                'A new version was deployed. Refresh to load the latest assets.',
            }),
            suggestions: [
              i18n.t('errorBoundary.chunkSuggestion1', {
                defaultValue: 'Click refresh below (or Ctrl/Cmd+Shift+R).',
              }),
            ],
          }
        : this.state.error
          ? formatErrorForDisplay(this.state.error)
          : {
              title: i18n.t('errorBoundary.defaultTitle'),
              message: i18n.t('errorBoundary.defaultMessage'),
              suggestions: [
                i18n.t('errorBoundary.suggestion1'),
                i18n.t('errorBoundary.suggestion2'),
                i18n.t('errorBoundary.suggestion3'),
              ],
            };

      return (
        <div className="error-boundary">
          <div className="error-boundary-content">
            <h2 className="error-boundary-title">{errorDisplay.title}</h2>
            <p className="error-boundary-message">{errorDisplay.message}</p>

            {errorDisplay.suggestions && errorDisplay.suggestions.length > 0 && (
              <div className="error-boundary-suggestions">
                <h3>{i18n.t('errorBoundary.whatYouCanDo')}</h3>
                <ul>
                  {errorDisplay.suggestions.map((suggestion, idx) => (
                    <li key={idx}>{suggestion}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="error-boundary-actions">
              {!chunkFail && (
                <button onClick={this.handleReset} className="error-boundary-button">
                  {i18n.t('errorBoundary.tryAgain')}
                </button>
              )}
              <button
                onClick={() => {
                  try {
                    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
                  } catch {
                    /* ignore */
                  }
                  window.location.reload();
                }}
                className="error-boundary-button error-boundary-button-secondary"
              >
                {i18n.t('errorBoundary.refreshPage')}
              </button>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
              <details className="error-boundary-details">
                <summary>{i18n.t('errorBoundary.devDetails')}</summary>
                <pre>{this.state.error && this.state.error.toString()}</pre>
                <pre>{this.state.errorInfo.componentStack}</pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
